import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, Typography, Grid, TextField, InputAdornment, IconButton } from "@material-ui/core";
import Stack from '@mui/material/Stack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useDispatch, useSelector } from "react-redux";
import "../../App.css";
import logo from "../../assets/img/login.png";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPassword } from "../../store/action/auth";

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        height: "100vh",
        fontFamily: '"Poppins", sans-serif',
        overflow: 'hidden',
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    leftSection: {
        display: "flex",
        width: "100%",
        backgroundImage: `url(${logo})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        height: "100vh",
        minHeight: "100vh",
        [theme.breakpoints.down("sm")]: {
            minHeight: "auto",
        },
    },
    rightSection: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "left",
        justifyContent: "center",
        padding: theme.spacing(2),
        height: "100vh",
        width: '100%',
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1),
        },
    },
    textHeader: {
        marginBottom: theme.spacing(5),
        textAlign: "left",
        color: "#30385A",
        marginTop: theme.spacing(6),
        alignContent: "left",
    },
    textHeader1: {
        fontSize: 30,
        fontFamily: "Poppins",
        fontWeight: 600,
        textWrap: "nowrap",
        color: 'rgb(47, 48, 68)',
        alignContent: "left",
    },
    textHeader2: {
        fontSize: 18,
        fontWeight: 500,
        fontFamily: "Poppins",
        alignContent: "left",
    },
    inputContainer: {
        width: "100%",
        maxWidth: 400,
    },
    field: {
        marginBottom: theme.spacing(2),
    },
    label: {
        fontSize: 14,
        fontWeight: 600,
        color: "rgba(47, 48, 68, 1)",
        fontFamily: '"Poppins", sans-serif',
    },
    actionBtn: {
        width: 180,
        height: 48,
        borderRadius: theme.shape.borderRadius,
        color: "rgba(255, 255, 255, 1)",
        background: "rgba(187, 200, 8, 1)",
        fontSize: 15,
        marginTop: theme.spacing(2),
        fontFamily: '"Poppins", sans-serif',
        textTransform: 'none',
    },
    footer: {
        marginTop: theme.spacing(18),
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: 400,
    },
    footerText: {
        fontSize: 16,
        fontWeight: 600,
        color: "rgba(187, 200, 8, 1)",
        fontFamily: '"Poppins", sans-serif',
    },
    verificationCodeInput: {
        letterSpacing: '0.5em',
        textAlign: 'center',
    },
}));

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function ResetPassword() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const query = useQuery();
    const passwordResetState = useSelector((state) => state.auth.passwordReset);

    useEffect(() => {
        const emailFromQuery = query.get("email");
        if (emailFromQuery) {
            setEmail(emailFromQuery);
        } else {
            navigate('/forgot-password');
        }
    }, [navigate, query]);

    useEffect(() => {
        validateForm();
    }, [verificationCode, newPassword, confirmPassword]);

    const handleVerificationCodeChange = (event) => {
        const value = event.target.value;
        if (/^\d*$/.test(value) && value.length <= 6) {
            setVerificationCode(value);
        }
    };

    const handleNewPasswordChange = (event) => {
        setNewPassword(event.target.value);
    };

    const handleConfirmPasswordChange = (event) => {
        setConfirmPassword(event.target.value);
    };

    const validateForm = () => {
        const isCodeValid = verificationCode.length === 6;
        const isPasswordValid = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(newPassword);
        const doPasswordsMatch = newPassword === confirmPassword;
        setIsFormValid(isCodeValid && isPasswordValid && doPasswordsMatch);
    };

    const handleResetPassword = async () => {
        if (isFormValid) {
            try {
                setIsLoading(true);
                setError(null);
                
                const result = await dispatch(resetPassword(email, verificationCode, newPassword));
                
                if (result.success) {
                    toast.success("Votre mot de passe a été réinitialisé avec succès !");
                    navigate('/login');
                } else {
                    throw new Error(result.message || "Échec de la réinitialisation du mot de passe");
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || "Une erreur s'est produite lors de la réinitialisation du mot de passe";
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Grid container>
            <Grid item xs={12} md={6} className={classes.leftSection}></Grid>
            <Grid item sm={6} xs={12} md={4} className={classes.rightSection}>
                <Box className={classes.textHeader}>
                    <Typography className={classes.textHeader1}>
                        Réinitialisation du mot de passe
                    </Typography>
                    <Typography className={classes.textHeader2}>
                        <span style={{ color: 'rgb(167, 168, 187)' }}>Entrez le code de vérification envoyé à votre email et créez un nouveau mot de passe</span>
                    </Typography>
                </Box>
                <Box className={classes.inputContainer}>
                    <Box className={classes.field} style={{ marginBottom: '20px' }}>
                        <Typography className={classes.label}>Code de vérification</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            inputProps={{ 
                                style: { 
                                    fontSize: 15, 
                                    backgroundColor: "#EEF1F5", 
                                    borderRadius: 5,
                                    letterSpacing: '0.5em',
                                    textAlign: 'center'
                                },
                                maxLength: 6
                            }}
                            type="text"
                            value={verificationCode}
                            onChange={handleVerificationCodeChange}
                            className="TextField-without-border-radius"
                            placeholder="Entrez le code à 6 chiffres"
                            disabled={isLoading}
                        />
                    </Box>

                    <Box className={classes.field} style={{ marginBottom: '20px' }}>
                        <Typography className={classes.label}>Nouveau mot de passe</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            inputProps={{ 
                                style: { 
                                    fontSize: 15, 
                                    backgroundColor: "#EEF1F5", 
                                    borderRadius: 5 
                                }
                            }}
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={handleNewPasswordChange}
                            className="TextField-without-border-radius"
                            placeholder="Entrez le nouveau mot de passe"
                            disabled={isLoading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Typography variant="caption" style={{ color: 'rgb(167, 168, 187)', marginTop: '4px' }}>
                            Le mot de passe doit contenir au moins 8 caractères avec au moins un chiffre, une majuscule et une minuscule
                        </Typography>
                    </Box>

                    <Box className={classes.field}>
                        <Typography className={classes.label}>Confirmer le mot de passe</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            inputProps={{ 
                                style: { 
                                    fontSize: 15, 
                                    backgroundColor: "#EEF1F5", 
                                    borderRadius: 5 
                                }
                            }}
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            className="TextField-without-border-radius"
                            placeholder="Confirmez le nouveau mot de passe"
                            disabled={isLoading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {confirmPassword && newPassword !== confirmPassword && (
                            <Typography variant="caption" style={{ color: 'red', marginTop: '4px' }}>
                                Les mots de passe ne correspondent pas
                            </Typography>
                        )}
                    </Box>

                    {error && (
                        <span 
                            style={{ 
                                color: "white", 
                                backgroundColor: "red", 
                                width: "98%", 
                                borderRadius: "4px", 
                                marginTop: "0.25rem", 
                                padding: "8px", 
                                height: "auto", 
                                display: "inline-flex", 
                                textAlign: "center", 
                                justifyContent: "center"
                            }}
                        >
                            {error}
                        </span>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                        <Button
                            className={classes.actionBtn}
                            style={{ 
                                backgroundColor: '#BBC808',
                                fontFamily: '"Poppins", sans-serif',
                            }}
                            onClick={handleResetPassword}
                            disabled={!isFormValid || isLoading}
                        >
                            {isLoading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
                        </Button>
                        
                        <Button
                            style={{ 
                                fontFamily: '"Poppins", sans-serif',
                                color: 'rgba(47, 48, 68, 1)'
                            }}
                            onClick={() => navigate('/forgot-password')}
                        >
                            Retour
                        </Button>
                    </Box>
                </Box>
                <Box className={classes.footer}>
                    <Box sx={{ width: '100%', margin: 'auto' }}>
                        <Stack direction={{ xs: 'row', sm: 'row' }} spacing={{ xs: 1, sm: 2, md: 4 }} justifyContent="start" alignItems="start">
                            <Typography className={classes.footerText}>Conditions</Typography>
                            <Typography className={classes.footerText}>Plans</Typography>
                            <Typography className={classes.footerText}>Contactez-nous</Typography>
                        </Stack>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}

export default ResetPassword;